/**
 * Short Intelligence — Trading UI client. Read-only.
 *
 * Reads /api/v1/short-intelligence/* over same-origin nginx proxy.
 * Advisory-only. The engine guarantees `short_execution_disabled: true` on
 * every row; the UI re-asserts this in the visible badge.
 *
 * Marker strings (must appear in dist for smoke grep):
 *   - "short-intelligence"
 *   - "phase-short-intel-ui-v1"
 */
import { apiV1Base } from "./apiConfig";

type Envelope<T> = {
  ok: boolean;
  data: T | null;
  error?: { message: string; code: string } | null;
};

export interface ShortCandidate {
  ticker?: string | null;
  company_name?: string | null;
  current_loss_pct?: number | null;
  why_falling?: string | null;
  short_type?: string | null;
  breakdown_score?: number | null;
  catalyst_risk_score?: number | null;
  institutional_exit_score?: number | null;
  continuation_score?: number | null;
  short_conviction_score?: number | null;     // 0-100 composite
  short_squeeze_risk_score?: number | null;   // 0-100
  short_thesis?: string | null;
  squeeze_warning?: string | null;            // prominent label when present
  best_action?: string | null;
  invalidation_level?: string | null;
  beginner_explanation?: string | null;
  data_quality?: string | null;
  missing_inputs?: string[] | null;
  sector?: string | null;
  catalyst_severity?: string | null;
  short_interest_pct?: number | null;
  float_size?: number | null;
  meme_signal?: string | null;
  advisory_only?: boolean | null;
  no_order_execution?: boolean | null;
  no_live_trading?: boolean | null;
  short_execution_disabled?: boolean | null;
}

export interface ShortIntelligencePayload {
  generated_at?: string | null;
  generated_at_et?: string | null;
  run_id?: string | null;
  candidates?: ShortCandidate[];
  count?: number | null;
  notes?: string | null;
}

export interface ShortIntelligenceHealth {
  status?: string | null;
  last_run_at?: string | null;
  last_run_at_et?: string | null;
  candidate_count?: number | null;
  message?: string | null;
}

export interface CouncilShortLane {
  symbol?: string | null;
  short_thesis?: string | null;
  short_conviction_score?: number | null;
  short_squeeze_risk_score?: number | null;
  squeeze_warning?: string | null;
  advisory_only?: boolean | null;
}

async function siGet<T>(path: string): Promise<Envelope<T>> {
  try {
    const res = await fetch(`${apiV1Base()}${path}`, {
      headers: { accept: "application/json" },
      credentials: "include",
      cache: "no-store",
    });
    if (!res.ok) {
      return {
        ok: false,
        data: null,
        error: { message: `HTTP ${res.status}`, code: String(res.status) },
      };
    }
    return (await res.json()) as Envelope<T>;
  } catch (err: any) {
    return {
      ok: false,
      data: null,
      error: { message: err?.message || "network", code: "NETWORK" },
    };
  }
}

export const getShortIntelligenceLatest = () =>
  siGet<ShortIntelligencePayload>("/short-intelligence/latest");

export const getShortIntelligenceCandidates = () =>
  siGet<ShortIntelligencePayload>("/short-intelligence/candidates");

export const getTopShorts = () =>
  siGet<ShortIntelligencePayload>("/short-intelligence/top-shorts");

export const getInstitutionalExits = () =>
  siGet<ShortIntelligencePayload>("/short-intelligence/institutional-exits");

export const getAIHypeRisk = () =>
  siGet<ShortIntelligencePayload>("/short-intelligence/ai-hype-risk");

export const getEarningsDisasters = () =>
  siGet<ShortIntelligencePayload>("/short-intelligence/earnings-disasters");

export const getSqueezeRisk = () =>
  siGet<ShortIntelligencePayload>("/short-intelligence/squeeze-risk");

export const getShortIntelligenceHealth = () =>
  siGet<ShortIntelligenceHealth>("/short-intelligence/health");

export const getCouncilShortLane = (symbol: string) =>
  siGet<CouncilShortLane>(
    `/short-intelligence/council-short-lane/${encodeURIComponent(symbol)}`,
  );
